import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { projectApi } from '../api/endpoints';
import { useAuth } from '../auth/AuthContext';
import type { Project } from '../types';

interface ProjectContextValue {
  projects: Project[];
  selectedProjectId: string | null;
  selectedProject: Project | null;
  setSelectedProjectId: (id: string | null) => void;
  loading: boolean;
  /** Nạp lại danh sách dự án. */
  refresh: () => Promise<void>;
}

const ProjectContext = createContext<ProjectContextValue | undefined>(undefined);

/**
 * Quản lý danh sách dự án + dự án đang chọn (ô chọn Project trên AppBar).
 * Chia sẻ cho mọi trang qua useProjects().
 */
export function ProjectProvider({ children }: { children: ReactNode }): JSX.Element {
  const { isAuthenticated } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      // Dùng cho ô chọn project trên AppBar — lấy trọn (tối đa cho phép), không phân trang ở đây.
      const page = await projectApi.list({ limit: 200 });
      setProjects(page.items);
      // Giữ lựa chọn cũ CHỈ KHI dự án đó còn trong danh sách. Nếu chỉ kiểm tra
      // `prev == null`, một id đã mất hiệu lực (khách bị gỡ khỏi dự án, hoặc dự
      // án bị xoá mềm) sẽ được giữ nguyên: selectedProject thành null và ô chọn
      // trên AppBar hiện trống kèm cảnh báo out-of-range của MUI.
      setSelectedProjectId((prev) => {
        if (prev && page.items.some((p) => p.id === prev)) return prev;
        return page.items.length > 0 ? page.items[0].id : null;
      });
    } catch {
      // Backend chưa sẵn sàng hoặc không có quyền → danh sách rỗng.
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo<ProjectContextValue>(() => {
    const selectedProject =
      projects.find((p) => p.id === selectedProjectId) ?? null;
    return {
      projects,
      selectedProjectId,
      selectedProject,
      setSelectedProjectId,
      loading,
      refresh,
    };
  }, [projects, selectedProjectId, loading, refresh]);

  return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>;
}

export function useProjects(): ProjectContextValue {
  const ctx = useContext(ProjectContext);
  if (!ctx) {
    throw new Error('useProjects phải được dùng bên trong <ProjectProvider>.');
  }
  return ctx;
}
